import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Trophy, TrendingUp } from "lucide-react";
import { PlanGate } from "@/components/enterprise-gate";
import { useBranch } from "@/context/branch";
import { readJsonArray } from "@/lib/http";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function fmt(v: number) {
  return `₦${v.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

function PerformanceContent() {
  const { activeBranchId } = useBranch();
  const { data, isLoading } = useQuery({
    queryKey: ["people-staff-performance", activeBranchId],
    queryFn: async () => {
      const query = activeBranchId ? `?branchId=${activeBranchId}` : "";
      const res = await fetch(`${BASE}/api/people/staff-performance${query}`, { credentials: "include" });
      return readJsonArray<any>(res, "staff performance");
    },
  });

  if (isLoading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const sorted = [...(data ?? [])].sort((a, b) => b.totalRevenue - a.totalRevenue);
  const topEarner = sorted[0];
  const totalRevenue = sorted.reduce((s, p) => s + p.totalRevenue, 0);
  const totalSales = sorted.reduce((s, p) => s + p.totalSales, 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Top Performer</span>
              <Trophy className="w-4 h-4 text-amber-500" />
            </div>
            <div className="text-lg font-bold truncate">{topEarner?.name ?? "—"}</div>
            <div className="text-xs text-muted-foreground">{fmt(topEarner?.totalRevenue ?? 0)}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Total Sales (All Staff)</span>
              <TrendingUp className="w-4 h-4 text-blue-500" />
            </div>
            <div className="text-xl font-bold">{totalSales}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Total Revenue (All Staff)</span>
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="text-xl font-bold">{fmt(totalRevenue)}</div>
          </CardContent>
        </Card>
      </div>

      <div className="border rounded-md overflow-hidden">
        <div className="overflow-y-auto max-h-[600px]">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="w-[40px]">#</TableHead>
              <TableHead>Staff Member</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Total Sales</TableHead>
              <TableHead className="text-right">Total Revenue</TableHead>
              <TableHead className="text-right">This Month</TableHead>
              <TableHead className="text-right">Month Revenue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!sorted.length ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No staff data yet</TableCell></TableRow>
            ) : sorted.map((s, i) => (
              <TableRow key={s.id}>
                <TableCell className="text-muted-foreground text-sm">
                  {i === 0 ? <Trophy className="w-4 h-4 text-amber-500" /> : i + 1}
                </TableCell>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell><Badge variant="secondary" className="capitalize">{s.role}</Badge></TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${s.isActive ? "bg-emerald-100 text-emerald-800" : "bg-muted text-muted-foreground"}`}>
                    {s.isActive ? "Active" : "Inactive"}
                  </span>
                </TableCell>
                <TableCell className="text-right font-semibold">{s.totalSales}</TableCell>
                <TableCell className="text-right font-semibold">{fmt(s.totalRevenue)}</TableCell>
                <TableCell className="text-right">{s.monthSales}</TableCell>
                <TableCell className="text-right">{fmt(s.monthRevenue)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>
    </div>
  );
}

export default function PeopleStaffPerformance() {
  const { activeBranch } = useBranch();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Staff Performance — {activeBranch?.name ?? "All Branches"}</h1>
        <p className="text-muted-foreground mt-1">Sales rankings and revenue contribution for the selected location.</p>
      </div>
      <PlanGate allowedPlans={["pro", "enterprise"]} feature="Staff Performance Reports">
        <PerformanceContent />
      </PlanGate>
    </div>
  );
}
